import React, { useEffect, useState } from "react";
import axiosInstance from "../../../interceptors";
import InlineMetricBar from "../../../components/analytics/InlineMetricBar";
import FunnelChart from "../../../components/analytics/FunnelChart";
import GoalTracker from "../../../components/analytics/GoalTracker";
import MetricCard from "../../../components/analytics/MetricCard";

const TEMPLATES = [
  { id: "t1", title: "Hiring overview", desc: "Metric cards + funnel + trend + source quality" },
  { id: "t2", title: "Pipeline health", desc: "Per-job funnel + stage bottlenecks + velocity" },
  { id: "t3", title: "Source analytics", desc: "Source comparison + ROI + conversion by source" },
  { id: "t4", title: "DEI dashboard", desc: "Diversity at each stage + pass-through rate" },
  { id: "t5", title: "Recruiter performance", desc: "Activity leaderboard + response times" },
  { id: "t6", title: "Interviewer analysis", desc: "Pass rates + bias detection + load balance" },
  { id: "t7", title: "Hiring plan tracker", desc: "Goal progress + forecast + velocity trend" },
];

const WIDGET_TYPES = ["METRIC_CARD", "LINE_CHART", "BAR_CHART", "FUNNEL", "PIE", "TABLE", "HEATMAP", "GOAL_TRACKER", "SCORECARD"];
const METRICS = ["applications_count", "stage_conversion_rate", "pipeline_velocity", "active_pipeline", "time_to_hire", "time_to_fill", "time_in_stage", "source_quality", "offer_acceptance_rate", "avg_ai_score", "score_distribution", "quality_of_hire", "recruiter_activity", "interviewer_load", "interviewer_pass_rate", "response_rate", "diversity_pipeline"];
const DIMENSIONS = ["department", "source", "location", "recruiter_id", "interviewer_id", "stage"];
const TIME_RANGES = ["LAST_7D", "LAST_30D", "LAST_90D", "LAST_QUARTER", "THIS_YEAR"];

export default function Analytics() {
  // Backend scopes analytics by tenantId query param; read it from localStorage
  // rather than hardcoding so multi-tenant logins hit the right data.
  const tenantId = localStorage.getItem("tenantId") || "1";
  const [tab, setTab] = useState("dashboard");
  const [dashboards, setDashboards] = useState([]);
  const [activeDash, setActiveDash] = useState(null);
  const [widgets, setWidgets] = useState([]);
  const [funnel, setFunnel] = useState(null);
  const [goals, setGoals] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [reports, setReports] = useState([]);
  const [addWidget, setAddWidget] = useState(false);
  const [newWidget, setNewWidget] = useState({ widgetType: "METRIC_CARD", metricKey: "applications_count", timeRange: "LAST_30D", title: "" });
  const [toast, setToast] = useState(null);

  // Per-resource status keys: undefined | "loading" | "ok" | { error, status }.
  const [resourceStatus, setResourceStatus] = useState({});

  // Only show "no X yet" hints when the fetch has actually settled OK.
  // While loading: skip (avoids a flash). On error (esp. 403): skip too —
  // the ResourceBanner already explains what happened; an inline empty
  // hint on top of it is misleading ("No dashboards yet" reads like a
  // green-field state, not a permission failure).
  const settled = (key) => resourceStatus[key] === "ok";

  useEffect(() => { document.title = "Analytics - Ample Hire"; load(); }, []);

  const loadOne = async (key, url, setter) => {
    setResourceStatus((s) => ({ ...s, [key]: "loading" }));
    try {
      const r = await axiosInstance.get(url);
      setter(Array.isArray(r.data) ? r.data : (r.data || null));
      setResourceStatus((s) => ({ ...s, [key]: "ok" }));
    } catch (e) {
      setResourceStatus((s) => ({
        ...s,
        [key]: { error: e?.message || "Failed", status: e?.status || e?.response?.status || 0 },
      }));
    }
  };

  const load = async () => {
    const tq = `?tenantId=${encodeURIComponent(tenantId)}`;
    await Promise.all([
      loadOne("dashboards", `/common/analytics/dashboards${tq}`, setDashboards),
      loadOne("funnel", `/common/analytics/funnel${tq}`, setFunnel),
      loadOne("goals", `/common/analytics/goals${tq}`, setGoals),
      loadOne("alerts", `/common/analytics/alerts${tq}`, setAlerts),
      loadOne("reports", `/common/analytics/reports${tq}`, setReports),
    ]);
  };


  const flashToast = (msg, ms = 2500) => {
    setToast(msg);
    setTimeout(() => setToast(null), ms);
  };

  const openDashboard = async (id) => {
    try {
      const r = await axiosInstance.get(`/common/analytics/dashboards/${id}`);
      setActiveDash(r.data?.dashboard);
      setWidgets(r.data?.widgets || []);
    } catch (e) {
      flashToast(`Couldn't open dashboard — ${e?.message || "request failed"}`);
    }
  };

  const createDashboard = async () => {
    try {
      // Don't send tenantId — backend reads it from the JWT and rejects
      // mismatched client-supplied values (see TenantClaim.fromJwtOrReject).
      const r = await axiosInstance.post("/common/analytics/dashboards", { title: "New dashboard" });
      await load();
      openDashboard(r.data?.id);
      flashToast("Dashboard created");
    } catch (e) {
      flashToast(`Couldn't create dashboard — ${e?.data?.error || e?.message || "request failed"}`);
    }
  };

  const handleAddWidget = async () => {
    if (!activeDash) return;
    try {
      await axiosInstance.post(`/common/analytics/dashboards/${activeDash.id}/widgets`, {
        ...newWidget, title: newWidget.title || newWidget.metricKey.replace(/_/g, " "),
        dashboardId: activeDash.id, width: 2, height: 1,
      });
      openDashboard(activeDash.id);
      setAddWidget(false);
      flashToast("Widget added");
    } catch (e) {
      flashToast(`Couldn't add widget — ${e?.data?.error || e?.message || "request failed"}`);
    }
  };

  return (
    <div className="page-enter">
      <div className="flex items-end justify-between mb-4">
        <div>
          <h1 className="text-2xl font-semibold text-vf-base">Analytics</h1>
          <p className="text-sm text-vf-muted mt-1">Customizable dashboards, reports, and alerts — Ashby-style analytics everywhere</p>
        </div>
        <div className="flex gap-2">
          <button onClick={createDashboard} className="px-3 py-1.5 rounded bg-vf-primary text-white text-xs font-medium">+ New dashboard</button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-slate-200 mb-4">
        {[["dashboard", "Dashboards"], ["reports", "Reports"], ["alerts", "Alerts"], ["goals", "Hiring goals"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === k ? "border-vf-primary text-vf-primary" : "border-transparent text-vf-muted"}`}>{l}</button>
        ))}
      </div>

      {/* Inline overview always visible */}
      <InlineMetricBar context="DASHBOARD" />

      <ResourceBanner statuses={resourceStatus} onRetry={load} />


      {tab === "dashboard" && (
        <div>
          {/* Dashboard selector */}
          <div className="flex gap-2 mb-4 flex-wrap">
            {dashboards.map(d => (
              <button key={d.id} onClick={() => openDashboard(d.id)}
                className={`px-3 py-1.5 rounded text-xs border ${activeDash?.id === d.id ? "bg-vf-tint border-vf-highlight text-vf-primary font-medium" : "border-slate-200 text-vf-muted"}`}>
                {d.title}
              </button>
            ))}
            {settled("dashboards") && dashboards.length === 0 && (
              <span className="text-xs text-vf-muted">No dashboards yet — create one or load a template</span>
            )}
          </div>

          {/* Templates */}
          <div className="mb-6">
            <h3 className="text-xs font-semibold text-vf-muted uppercase mb-2">Templates (click to load)</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {TEMPLATES.map(t => (
                <button key={t.id} className="text-left bg-white border border-slate-200 rounded-lg p-3 hover:border-vf-highlight transition-all">
                  <div className="text-xs font-medium text-vf-base">{t.title}</div>
                  <div className="text-[10px] text-vf-muted mt-0.5">{t.desc}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Active dashboard widgets */}
          {activeDash && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-vf-base">{activeDash.title}</h3>
                <button onClick={() => setAddWidget(true)} className="px-2 py-1 rounded border border-dashed border-vf-highlight text-xs text-vf-primary">+ Add widget</button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                {widgets.map(w => (
                  <div key={w.id} className={`bg-white border border-slate-200 rounded-lg p-4 ${w.width >= 4 ? "lg:col-span-4" : w.width >= 2 ? "lg:col-span-2" : ""}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium text-vf-base">{w.title}</span>
                      <span className="text-[10px] text-vf-muted">{w.widgetType}</span>
                    </div>
                    {w.widgetType === "METRIC_CARD" && <MetricCard value={72} label={w.metricKey?.replace(/_/g, " ")} trend={5} size="md" />}
                    {w.widgetType === "FUNNEL" && funnel && <FunnelChart stages={funnel.stages} />}
                    {!["METRIC_CARD", "FUNNEL"].includes(w.widgetType) && (
                      <div className="text-xs text-vf-muted py-6 text-center border border-dashed border-slate-200 rounded">{w.widgetType} visualization for {w.metricKey}</div>
                    )}
                  </div>
                ))}
                {widgets.length === 0 && <div className="lg:col-span-4 text-center text-sm text-vf-muted py-8 border border-dashed border-slate-200 rounded-xl">Add widgets to build your dashboard</div>}
              </div>
            </div>
          )}

          {/* Funnel + Goals */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-6">
            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <h3 className="text-sm font-semibold text-vf-base mb-3">Overall pipeline funnel</h3>
              {funnel ? (
                <FunnelChart stages={funnel.stages} />
              ) : settled("funnel") ? (
                <div className="text-xs text-vf-muted py-6 text-center">No funnel data yet.</div>
              ) : (
                <div className="text-xs text-vf-muted py-6 text-center">Loading funnel...</div>
              )}
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <h3 className="text-sm font-semibold text-vf-base mb-3">Hiring goals</h3>
              {goals.length > 0 ? (
                <GoalTracker goals={goals} />
              ) : settled("goals") ? (
                <div className="text-xs text-vf-muted py-6 text-center">No hiring goals set. Create one in the Goals tab.</div>
              ) : null}
            </div>
          </div>
        </div>
      )}

      {tab === "reports" && (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-vf-base">Saved reports & templates</h3>
          {reports.length === 0 ? (
            settled("reports") ? (
              <div className="text-center text-sm text-vf-muted py-12 bg-white border border-dashed border-slate-200 rounded-xl">No reports yet. Build one from a template above.</div>
            ) : null
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {reports.map(r => (
                <div key={r.id} className="bg-white border border-slate-200 rounded-lg p-4">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-vf-base">{r.title}</span>
                    {r.isTemplate && <span className="text-[10px] bg-vf-tint text-vf-primary px-1.5 py-0.5 rounded-full">Template</span>}
                    {r.templateCategory && <span className="text-[10px] text-vf-muted">{r.templateCategory}</span>}
                  </div>
                  <div className="text-xs text-vf-muted mt-1">{r.reportType} · {r.metrics}</div>
                  <button onClick={() => axiosInstance.post(`/common/analytics/reports/${r.id}/run`)} className="mt-2 text-xs text-vf-primary hover:underline">Run report</button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "alerts" && (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-vf-base">Active alerts</h3>
          {alerts.length === 0 ? (
            settled("alerts") ? (
              <div className="text-center text-sm text-vf-muted py-12 bg-white border border-dashed border-slate-200 rounded-xl">No alerts configured. Create one to monitor key metrics.</div>
            ) : null
          ) : alerts.map(a => (
            <div key={a.id} className={`bg-white border rounded-lg p-4 ${a.isTriggered ? "border-red-300" : "border-slate-200"}`}>
              <div className="flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full ${a.isTriggered ? "bg-red-500" : a.isActive ? "bg-emerald-500" : "bg-slate-300"}`} />
                <span className="text-sm font-medium text-vf-base">{a.title}</span>
                <span className="text-[10px] text-vf-muted">{a.metricKey} {a.condition} {String(a.threshold)}</span>
              </div>
              {a.currentValue !== null && <div className="text-xs text-vf-muted mt-1">Current: {String(a.currentValue)} | Check: {a.checkInterval}</div>}
            </div>
          ))}
        </div>
      )}

      {tab === "goals" && (
        <div>
          <h3 className="text-sm font-semibold text-vf-base mb-3">Hiring goals</h3>
          <GoalTracker goals={goals} />
          {goals.length === 0 && settled("goals") && (
            <div className="text-center text-sm text-vf-muted py-12 bg-white border border-dashed border-slate-200 rounded-xl mt-3">No goals set. Create hiring targets to track progress.</div>
          )}
        </div>
      )}

      {/* Add widget slide-over */}
      {addWidget && (
        <div className="fixed inset-0 bg-black/40 z-50 flex justify-end" onClick={() => setAddWidget(false)}>
          <div className="bg-white w-full max-w-md h-full overflow-y-auto p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-vf-base">Add widget</h2>
              <button onClick={() => setAddWidget(false)} className="text-vf-muted hover:text-vf-base text-xl">×</button>
            </div>
            <div className="space-y-4">
              <label className="block"><span className="text-xs font-medium text-vf-muted uppercase">Widget type</span>
                <select value={newWidget.widgetType} onChange={e => setNewWidget(w => ({ ...w, widgetType: e.target.value }))} className="mt-1 w-full px-3 py-2 text-sm border border-slate-200 rounded">
                  {WIDGET_TYPES.map(t => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
                </select>
              </label>
              <label className="block"><span className="text-xs font-medium text-vf-muted uppercase">Metric</span>
                <select value={newWidget.metricKey} onChange={e => setNewWidget(w => ({ ...w, metricKey: e.target.value }))} className="mt-1 w-full px-3 py-2 text-sm border border-slate-200 rounded">
                  {METRICS.map(m => <option key={m} value={m}>{m.replace(/_/g, " ")}</option>)}
                </select>
              </label>
              <label className="block"><span className="text-xs font-medium text-vf-muted uppercase">Time range</span>
                <select value={newWidget.timeRange} onChange={e => setNewWidget(w => ({ ...w, timeRange: e.target.value }))} className="mt-1 w-full px-3 py-2 text-sm border border-slate-200 rounded">
                  {TIME_RANGES.map(t => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
                </select>
              </label>
              <label className="block"><span className="text-xs font-medium text-vf-muted uppercase">Title (optional)</span>
                <input value={newWidget.title} onChange={e => setNewWidget(w => ({ ...w, title: e.target.value }))} placeholder="Auto-generated from metric" className="mt-1 w-full px-3 py-2 text-sm border border-slate-200 rounded" />
              </label>
              <button onClick={handleAddWidget} className="w-full px-3 py-2 rounded bg-vf-primary text-white text-sm font-medium">Add to dashboard</button>
            </div>
          </div>
        </div>
      )}

      {toast && <div className="fixed bottom-6 right-6 z-[9999] px-4 py-2.5 rounded-lg shadow-lg text-sm font-medium bg-vf-base text-white">{toast}</div>}
    </div>
  );
}

// Renders a per-failed-resource notice. Distinguishes forbidden (restricted),
// not found (endpoint missing), unavailable (network/5xx), and generic 4xx.
function ResourceBanner({ statuses, onRetry }) {
  const failed = Object.entries(statuses || {})
    .filter(([, v]) => v && typeof v === "object" && v.error)
    .map(([key, v]) => ({ key, ...v }));
  if (failed.length === 0) return null;

  return (
    <div className="space-y-2 mb-4">
      {failed.map((f) => (
        <div
          key={f.key}
          className={`px-3 py-2 rounded border text-xs flex items-center justify-between ${
            f.status === 403
              ? "border-vf-tint bg-vf-tint/30 text-vf-base"
              : f.status === 404
              ? "border-slate-200 bg-slate-50 text-vf-muted"
              : f.status === 0 || f.status >= 500
              ? "border-amber-200 bg-amber-50 text-amber-800"
              : "border-red-200 bg-red-50 text-red-700"
          }`}
        >
          <span>
            <b className="capitalize">{f.key}</b>:{" "}
            {f.status === 403
              ? "Analytics is restricted to admin users in your workspace. Contact your workspace admin to enable access."
              : f.status === 404
              ? "Endpoint not available yet — this tab will populate once the backend lands."
              : f.status === 0
              ? "Analytics service is unavailable right now."
              : `Couldn't load (HTTP ${f.status || "?"})`}
          </span>
          <button
            type="button"
            onClick={onRetry}
            className="text-[11px] px-2 py-0.5 rounded border border-slate-300 ml-3"
          >
            Retry
          </button>
        </div>
      ))}
    </div>
  );
}
